// ============================================================
// SpawnDialog - Add a Character to the roster.
//
// v7.4.0: under the unified model there is no "load as attacker" vs
// "load as target" - there is ONE combatant, and two private pointers
// that may or may not point at them. So this dialog adds the person,
// and the two toggles decide which of your pointers move. Ticking both
// is self-targeting, and still one combatant; the old "Load as Both"
// ran two separate load paths and produced a duplicate.
//
// Preset count buttons (1/3/5/custom) and horde magnitude are unchanged.
// ============================================================
import { useState } from 'react';
import type { Character } from '../engine/character/types';
import { standingSummary } from '../engine/roster/standing';
import useFocusTrap from '../hooks/useFocusTrap.js';
import common from '../styles/common.module.css';
import styles from '../styles/SpawnDialog.module.css';
import clsx from '../utils/clsx';
import NumberInput from './NumberInput';
import BattlefieldModal from './BattlefieldModal';

interface SpawnPointers {
  /** Point "who I'm acting as" at the new combatant. */
  actAs: boolean;
  /** Point "who I'm targeting" at the new combatant. */
  target: boolean;
  /** Restore the entry's saved condition, when it has one and one combatant is added. */
  asStands: boolean;
  /** Born hidden: kept off every player's machine until the GM reveals them (plan map-display, G1). */
  hidden: boolean;
}

interface SpawnDialogProps {
  character: Character;
  onConfirm: (count: number, magnitude: number | undefined, pointers: SpawnPointers) => void;
  onCancel: () => void;
  /** The GM, in a session: may add them hidden from the players. */
  canHide?: boolean;
}

const PRESETS = [1, 3, 5] as const;

/** How many to add: a preset, or a typed count once the field is used (never fewer than one). */
function useSpawnCount() {
  const [count, setCount] = useState(1);
  const [customCount, setCustomCount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const pickPreset = (n: number) => {
    setCount(n);
    setIsCustom(false);
    setCustomCount('');
  };
  const typeCustom = (val: string) => {
    setCustomCount(val);
    setIsCustom(true);
  };
  const effectiveCount = isCustom ? Math.max(1, parseInt(customCount, 10) || 1) : count;
  return { count, customCount, isCustom, setIsCustom, pickPreset, typeCustom, effectiveCount };
}

type SpawnCount = ReturnType<typeof useSpawnCount>;

function CountSection({ spawn }: { spawn: SpawnCount }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>Spawn count</div>
      <div className={styles.presetRow}>
        {PRESETS.map((n) => (
          <button
            key={n}
            className={clsx(styles.presetBtn, !spawn.isCustom && spawn.count === n && styles.presetBtnActive)}
            onClick={() => spawn.pickPreset(n)}
            type="button"
          >
            {n}
          </button>
        ))}
        <span className={styles.customLabel}>or</span>
        <input
          className={styles.customInput}
          type="number"
          min={1}
          max={99}
          value={spawn.isCustom ? spawn.customCount : ''}
          placeholder="#"
          onFocus={() => spawn.setIsCustom(true)}
          onChange={(e) => spawn.typeCustom(e.target.value)}
          aria-label="Custom spawn count"
        />
      </div>
    </div>
  );
}

/** Horde magnitude, only for horde characters. */
function MagnitudeSection({
  max,
  magnitude,
  onMagnitude,
}: {
  max: number;
  magnitude: number;
  onMagnitude: (v: number) => void;
}) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>Horde magnitude</div>
      <div className={styles.magnitudeRow}>
        <NumberInput
          className={styles.magnitudeInput}
          min={1}
          max={max}
          value={magnitude}
          onChange={(value: number) => onMagnitude(value)}
          aria-label="Horde magnitude"
        />
        <span className={styles.magnitudeMax}>/ {max}</span>
      </div>
    </div>
  );
}

/** One "after adding" switch. */
function PointerCheck({
  checked,
  onFlip,
  testId,
  children,
}: {
  checked: boolean;
  onFlip: () => void;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={clsx(common.label, common.checkRow)}>
      <input type="checkbox" checked={checked} onChange={onFlip} data-testid={testId} />
      {children}
    </label>
  );
}

/** As they stand, or fresh: asked whenever the entry carries a saved condition. */
function StandingSection({
  summary,
  asStands,
  onPick,
}: {
  summary: string;
  asStands: boolean;
  onPick: (asStands: boolean) => void;
}) {
  return (
    <div className={styles.section} role="radiogroup" aria-label="Condition">
      <div className={styles.sectionLabel}>Condition</div>
      <label className={clsx(common.label, common.checkRow)}>
        <input type="radio" name="spawn-standing" checked={asStands} onChange={() => onPick(true)} />
        As they stand ({summary})
      </label>
      <label className={clsx(common.label, common.checkRow)}>
        <input type="radio" name="spawn-standing" checked={!asStands} onChange={() => onPick(false)} />
        Fresh
      </label>
    </div>
  );
}

/** Escape cancels and Enter adds, from anywhere in the dialog. */
function keysFor(onCancel: () => void, confirm: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
    if (e.key === 'Enter') confirm();
  };
}

/** A one-line summary under the title: wounds, horde, size when not Average. */
function summaryOf(character: Character): string {
  return [
    `${character.wounds.max} wounds`,
    character.horde.isHorde ? 'Horde' : null,
    character.size !== 'Average' ? character.size : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface Toggle {
  on: boolean;
  flip: () => void;
}

interface AfterProps {
  whom: string;
  actAs: Toggle;
  targetThem: Toggle;
  /** Offered to the GM in a session only; null hides the check. */
  hidden: Toggle | null;
}

/**
 * After adding: which of MY private pointers move (both is self-targeting, and
 * still one combatant), and - for the GM - whether the players may see them.
 */
function AfterSection({ whom, actAs, targetThem, hidden }: AfterProps) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>After adding</div>
      <PointerCheck checked={actAs.on} onFlip={actAs.flip}>
        Act as {whom}
      </PointerCheck>
      <PointerCheck checked={targetThem.on} onFlip={targetThem.flip}>
        Target {whom}
      </PointerCheck>
      {hidden && (
        <PointerCheck checked={hidden.on} onFlip={hidden.flip} testId="spawn-hidden">
          Hidden from players
        </PointerCheck>
      )}
    </div>
  );
}

/** A checkbox's state and its flip, as one value. */
function useToggle(initial: boolean): Toggle {
  const [on, setOn] = useState(initial);
  return { on, flip: () => setOn((v) => !v) };
}

export default function SpawnDialog({ character, onConfirm, onCancel, canHide = false }: SpawnDialogProps) {
  const spawn = useSpawnCount();
  const [magnitude, setMagnitude] = useState(character.horde.maxMagnitude);
  const actAs = useToggle(true);
  const targetThem = useToggle(true);
  const hidden = useToggle(false);
  const [asStands, setAsStands] = useState(true);
  const trapRef = useFocusTrap(true);
  const isHorde = character.horde.isHorde;
  const n = spawn.effectiveCount;
  // A saved condition belongs to ONE person: several copies start fresh.
  const saved = n === 1 ? standingSummary(character) : null;
  const standing = !!saved && asStands;
  // As they stand, the saved magnitude is the magnitude: the picker would be overruled, so it steps aside.
  const picksMagnitude = isHorde && !standing;
  const confirm = () =>
    onConfirm(n, picksMagnitude ? magnitude : undefined, {
      actAs: actAs.on,
      target: targetThem.on,
      asStands: standing,
      hidden: canHide && hidden.on,
    });
  const onKeyDown = keysFor(onCancel, confirm);
  const summary = summaryOf(character);
  const whom = n > 1 ? 'the first of them' : 'them';
  return (
    <BattlefieldModal
      onBackdropClick={onCancel}
      onKeyDown={onKeyDown}
      trapRef={trapRef}
      className={clsx(common.modalContent, styles.content)}
      label="Add to roster dialog"
    >
      <h3 className={styles.title}>Add &ldquo;{character.name}&rdquo; to the roster</h3>
      {summary && <div className={styles.subtitle}>{summary}</div>}
      <CountSection spawn={spawn} />
      {picksMagnitude && (
        <MagnitudeSection max={character.horde.maxMagnitude} magnitude={magnitude} onMagnitude={setMagnitude} />
      )}
      {saved && <StandingSection summary={saved} asStands={asStands} onPick={setAsStands} />}
      <AfterSection whom={whom} actAs={actAs} targetThem={targetThem} hidden={canHide ? hidden : null} />
      <div className={styles.footer}>
        <button className={common.btnSecondary} onClick={onCancel} type="button">
          Cancel
        </button>
        {/* Deliberately NOT "Add to roster" - that is the library button's
            label, and Playwright's has-text is a substring match (see
            dev-docs/gotchas.md). */}
        <button className={common.btnPrimary} onClick={confirm} type="button">
          Add {n} combatant{n > 1 ? 's' : ''}
        </button>
      </div>
    </BattlefieldModal>
  );
}
